import { useState } from "react";
import CookieConsent from "react-cookie-consent";
import styles from "./CookieBanner.module.scss";
import { useCookieContext } from "../../context/CookieContext";
import { updateGtagConsent } from "../../utils/updateGtagConsent";
import { initGTM } from "../../utils/initGTM";
import { CookieSettings, ConsentStatus } from "../../types/cookie";

const CookieBanner = () => {
  const { isOpen, closeBanner, cookieSettings, updateCookieSettings } =
    useCookieContext();
  const [showSettings, setShowSettings] = useState(false);
  const [tempSettings, setTempSettings] =
    useState<CookieSettings>(cookieSettings);

  const handleConsent = (status: ConsentStatus, settings?: CookieSettings) => {
    let finalSettings: CookieSettings;

    if (status === "accepted") {
      finalSettings = {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      };
    } else if (status === "declined") {
      finalSettings = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      };
    } else {
      finalSettings = settings || cookieSettings;
    }

    // Використовуємо функцію з контексту
    updateCookieSettings(finalSettings);
    localStorage.setItem("cookieConsent", status);
    updateGtagConsent(finalSettings);

    if (finalSettings.analytics) {
      initGTM();
    }

    closeBanner();
    setShowSettings(false);
  };

  const toggleSetting = (key: keyof CookieSettings) => {
    if (key === "necessary") return;
    setTempSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {isOpen && !showSettings && (
        <CookieConsent
          disableStyles={true}
          location="bottom"
          cookieName="cookieConsent"
          containerClasses={styles.cookieConsent}
          contentClasses={styles.content}
          // Приховуємо дефолтні кнопки
          buttonText={null}
          declineButtonText={null}
          enableDeclineButton={false}
          expires={365}
          visible={isOpen ? "show" : "hidden"}
        >
          <div className={styles.bannerContent}>
            <strong>Cookie-Einstellungen</strong>
            <p className={styles.text}>
              Wir verwenden Cookies, um die Nutzung unserer Website zu
              verbessern. Wenn Sie auf unserer Website bleiben, stimmen Sie der
              Verwendung von Cookies zu.{" "}
              <a href="/datenschutzerklarung" className={styles.link}>
                Datenschutzerklärung
              </a>
            </p>
            <div className={styles.customButtons}>
              <button
                className={styles.acceptButton}
                onClick={() => handleConsent("accepted")}
              >
                Alles akzeptieren
              </button>
              <button
                className={styles.declineButton}
                onClick={() => handleConsent("declined")}
              >
                Ablehnen
              </button>
              <button
                className={styles.customizeButton}
                onClick={() => {
                  setTempSettings(cookieSettings);
                  setShowSettings(true);
                }}
              >
                Anpassen
              </button>
            </div>
          </div>
        </CookieConsent>
      )}

      {showSettings && (
        <div className={styles.settingsModal}>
          <div className={styles.settingsContent}>
            <h3>Cookie-Einstellungen</h3>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>
                  Erforderliche Cookies
                </span>
                <label className={styles.toggleSwitch}>
                  <input type="checkbox" checked={true} disabled />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                Notwendig für die Funktion der Website. Können nicht deaktiviert
                werden.
              </p>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>Analytische Cookies</span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={tempSettings.analytics}
                    onChange={() => toggleSetting("analytics")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                Helfen, die Website zu verbessern, indem anonyme Statistiken
                gesammelt werden.
              </p>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>Marketing-Cookies</span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={tempSettings.marketing}
                    onChange={() => toggleSetting("marketing")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                Werden verwendet, um relevante Werbung anzuzeigen und
                Integrationen mit Diensten (Google Maps, YouTube) zu
                ermöglichen.
              </p>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingHeader}>
                <span className={styles.settingTitle}>Präferenz-Cookies</span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={tempSettings.preferences}
                    onChange={() => toggleSetting("preferences")}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
              <p className={styles.settingDescription}>
                Speichern Ihre Einstellungen und Präferenzen auf der Website.
              </p>
            </div>

            <div className={styles.settingsButtons}>
              <button
                className={styles.saveButton}
                onClick={() => handleConsent("custom", tempSettings)}
              >
                Einstellungen speichern
              </button>
              <button
                className={styles.cancelButton}
                onClick={() => setShowSettings(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
